import React, { useState, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Select,
  useToast,
  useDisclosure,
  Alert,
  AlertIcon,
  HStack,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Progress,
  Box,
  Radio,
  RadioGroup,
  Badge,
} from '@chakra-ui/react';
import { CreatableSelect } from 'chakra-react-select';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import { functionsUsEast1 } from '../../utils/init-firebase';
import { httpsCallable } from 'firebase/functions';
import LostDealReasonModal from './LostDealReasonModal';
import { syncTagOwnershipOnReassignment } from '../../utils/tagOwnership';
import { applyTagsBulkByDeal } from '../../utils/crmTags';
import { useTagRemovalConfirm } from './ConfirmTagRemovalDialog';

export function BulkStageModal({ isOpen, onClose, selectedDeals, onSuccess, isDTCLoanOfficer = false }) {
  const [selectedStage, setSelectedStage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser } = useAuth();
  // visibleDealStages = hidden removed (for the picker); dealStages = full list (for label lookup)
  const { visibleDealStages, dealStages } = useCrmSettings();
  const toast = useToast();
  
  // Lost Deal Reason Modal state (for DTC Loan Officers)
  const { isOpen: isLostReasonOpen, onOpen: onLostReasonOpen, onClose: onLostReasonClose } = useDisclosure();
  const [isLostReasonSubmitting, setIsLostReasonSubmitting] = useState(false);

  // Core update function (extracted for reuse)
  const performBulkUpdate = async (lostReasonData = null) => {
    setIsUpdating(true);
    setProgress(0);
    
    try {
      if (isSupabaseConfigured()) {
        // Update deals in Supabase
        const { error: dealsError } = await supabase
          .from('crm_deals')
          .update({ stage: selectedStage })
          .in('id', selectedDeals);

        if (dealsError) throw dealsError;

        // Add audit log entry
        await supabase.from('crm_audit_logs').insert({
          action: 'bulk_stage_update',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'deal',
          details: {
            dealCount: selectedDeals.length,
            newStage: selectedStage,
            stageName: dealStages.find(s => s.value === selectedStage)?.label || selectedStage,
            dealIds: selectedDeals,
            ...(lostReasonData && {
              lostReason: lostReasonData.reason,
              lostReasonLabel: lostReasonData.reasonLabel,
              lostReasonDetails: lostReasonData.details,
            }),
          },
        });
      }

      toast({
        title: 'Stages updated',
        description: `Updated ${selectedDeals.length.toLocaleString()} deals`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      setSelectedStage('');
      onClose();
    } catch (error) {
      console.error('Error updating stages:', error);
      toast({
        title: 'Error updating stages',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      setProgress(0);
    }
  };

  const handleUpdate = async () => {
    if (!selectedStage) {
      toast({
        title: 'Select a stage',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    // DTC Loan Officer Lost Deal Workflow - intercept Lost stage selection
    if (selectedStage === 'deal-lost' && isDTCLoanOfficer) {
      onLostReasonOpen();
      return; // Don't proceed until lost reason modal is completed
    }

    await performBulkUpdate();
  };

  // Handler for Lost Deal reason confirmation (DTC Loan Officers - bulk operation)
  const handleLostReasonConfirm = async ({ reason, reasonLabel, details }) => {
    setIsLostReasonSubmitting(true);
    
    try {
      // Send lost deal notification emails for each deal
      const sendLostDealNotification = httpsCallable(functionsUsEast1, 'sendLostDealNotification');
      
      // Process each deal and send notification
      let successCount = 0;
      let errorCount = 0;
      
      for (const dealId of selectedDeals) {
        try {
          await sendLostDealNotification({
            dealId,
            reason,
            reasonDetails: details,
            loanOfficerName: currentUser.displayName || currentUser.email,
            loanOfficerEmail: currentUser.email,
          });
          successCount++;
        } catch (emailError) {
          console.error(`Error sending notification for deal ${dealId}:`, emailError);
          errorCount++;
          // Continue processing other deals even if one fails
        }
      }
      
      // Close the lost reason modal
      onLostReasonClose();
      
      // Proceed with the bulk stage update
      await performBulkUpdate({ reason, reasonLabel, details });
      
      // Show summary toast if there were any email errors
      if (errorCount > 0) {
        toast({
          title: 'Some notifications failed',
          description: `${successCount} notifications sent, ${errorCount} failed`,
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Notifications sent',
          description: `Sent ${successCount} lost deal notifications`,
          status: 'info',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error processing bulk lost deals:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process lost deal notifications',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLostReasonSubmitting(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={isUpdating ? undefined : onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Stage (Bulk)</ModalHeader>
          {!isUpdating && <ModalCloseButton />}
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" size="sm">
                <AlertIcon />
                <Text fontSize="sm">This will update {selectedDeals.length.toLocaleString()} deals</Text>
              </Alert>

              <Select
                placeholder="Select new stage"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                isDisabled={isUpdating}
              >
                {visibleDealStages.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </Select>
              
              {isUpdating && progress > 0 && (
                <VStack spacing={1} align="stretch">
                  <Text fontSize="sm" color="gray.600">Updating deals... {progress}%</Text>
                  <Progress value={progress} size="sm" colorScheme="blue" borderRadius="md" />
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isLoading={isUpdating}
              loadingText={`Updating... ${progress}%`}
            >
              Update {selectedDeals.length.toLocaleString()} Deals
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* DTC Lost Deal Reason Modal for bulk operations */}
      <LostDealReasonModal
        isOpen={isLostReasonOpen}
        onClose={() => {
          onLostReasonClose();
        }}
        deal={null}
        contact={null}
        onConfirm={handleLostReasonConfirm}
        isLoading={isLostReasonSubmitting}
        dealCount={selectedDeals.length}
      />
    </>
  );
}

export function BulkTagModal({ isOpen, onClose, selectedDeals, onSuccess }) {
  const [selectedTags, setSelectedTags] = useState([]);
  const [mode, setMode] = useState('add');
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const toast = useToast();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { getVisibleTags } = useCrmSettings();
  const { confirmTagRemoval, dialog: removalDialog } = useTagRemovalConfirm();

  // Get whether user is admin for tag visibility
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  
  // Get visible tags for the current user
  const availableTags = useMemo(() => {
    const tags = getVisibleTags(currentUser?.uid, isCRMAdmin) || [];
    return tags.map(tag => ({
      value: tag.value,
      label: tag.label,
      color: tag.color || 'gray',
    }));
  }, [getVisibleTags, currentUser?.uid, isCRMAdmin]);

  const isRemoving = mode === 'remove';

  const handleUpdate = async () => {
    if (selectedTags.length === 0) {
      toast({
        title: 'Select at least one tag',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (isRemoving) {
      const dealCount = selectedDeals.length.toLocaleString();
      const confirmed = await confirmTagRemoval({
        title: `Remove ${selectedTags.length === 1 ? 'this tag' : 'these tags'} from ${dealCount} deals?`,
        tags: selectedTags.map(t => t.label || t.value),
        description: `This removes ${selectedTags.length === 1 ? 'the tag' : 'these tags'} from ${dealCount} deal(s) and each deal's linked contact.`,
        note: "Because tags are shared, the contacts' other deals lose them too. This cannot be undone.",
        confirmLabel: `Remove from ${dealCount} deals`,
      });

      if (!confirmed) return;
    }

    setIsUpdating(true);
    setProgress({ current: 0, total: selectedDeals.length });
    
    try {
      const tagValues = selectedTags.map(t => t.value);
      
      if (isSupabaseConfigured()) {
        // Chunked so each RPC call stays inside the statement timeout. The RPC
        // writes each selected deal (so deal-scoped campaigns still enroll) and
        // the shared tag set on its contact.
        const CHUNK_SIZE = 100;
        for (let i = 0; i < selectedDeals.length; i += CHUNK_SIZE) {
          const chunk = selectedDeals.slice(i, i + CHUNK_SIZE);
          await applyTagsBulkByDeal({
            dealIds: chunk,
            add: isRemoving ? [] : tagValues,
            remove: isRemoving ? tagValues : [],
            actorId: currentUser?.uid,
          });
          setProgress({ current: Math.min(i + CHUNK_SIZE, selectedDeals.length), total: selectedDeals.length });
        }

        // Add audit log
        await supabase.from('crm_audit_logs').insert({
          action: isRemoving ? 'bulk_tag_remove' : 'bulk_tag_add',
          user_id: currentUser?.uid,
          user_name: currentUser?.displayName || currentUser?.email,
          entity_type: 'deal',
          details: {
            dealCount: selectedDeals.length,
            [isRemoving ? 'tagsRemoved' : 'tagsAdded']: tagValues,
            dealIds: selectedDeals,
          },
        });
      }

      toast({
        title: isRemoving ? 'Tags removed' : 'Tags added',
        description: `${isRemoving ? 'Removed' : 'Added'} ${tagValues.length} tag(s) ${isRemoving ? 'from' : 'to'} ${selectedDeals.length.toLocaleString()} deals`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      setSelectedTags([]);
      onClose();
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        title: isRemoving ? 'Error removing tags' : 'Error adding tags',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <Modal isOpen={isOpen} onClose={isUpdating ? undefined : onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isRemoving ? 'Remove Tags (Bulk)' : 'Add Tags (Bulk)'}</ModalHeader>
        {!isUpdating && <ModalCloseButton />}
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <RadioGroup value={mode} onChange={setMode} isDisabled={isUpdating}>
              <HStack spacing={6}>
                <Radio value="add">Add tags</Radio>
                <Radio value="remove">Remove tags</Radio>
              </HStack>
            </RadioGroup>

            <Alert status={isRemoving ? 'warning' : 'info'} size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                {isRemoving ? (
                  <>Tags will be <strong>removed</strong> from {selectedDeals.length.toLocaleString()} deals and their linked contacts</>
                ) : (
                  <>Tags will be <strong>added</strong> to {selectedDeals.length.toLocaleString()} deals (existing tags are preserved)</>
                )}
              </Text>
            </Alert>

            <Text fontSize="sm" fontWeight="medium">
              {isRemoving ? 'Select tags to remove:' : 'Select tags to add:'}
            </Text>
            <CreatableSelect
              isMulti
              value={selectedTags}
              onChange={setSelectedTags}
              options={availableTags}
              placeholder={isRemoving ? 'Search tags...' : 'Search or create tags...'}
              isDisabled={isUpdating}
              isValidNewOption={() => !isRemoving}
              formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                }),
                menu: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                  zIndex: 9999,
                }),
                multiValue: (provided, state) => ({
                  ...provided,
                  backgroundColor: `var(--chakra-colors-${state.data.color || 'gray'}-100)`,
                }),
                multiValueLabel: (provided, state) => ({
                  ...provided,
                  color: `var(--chakra-colors-${state.data.color || 'gray'}-800)`,
                }),
              }}
            />
            <Text fontSize="xs" color="gray.500">
              {isRemoving
                ? 'Tags are shared between a deal and its contact, so removing here removes it from both.'
                : 'Type to search existing tags or create new custom tags'}
            </Text>
            
            {isUpdating && progress.total > 0 && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.600">{isRemoving ? 'Removing tags...' : 'Adding tags...'}</Text>
                  <Text fontSize="sm" fontWeight="medium">{progressPercent}%</Text>
                </HStack>
                <Box h="8px" bg="gray.200" borderRadius="full" overflow="hidden">
                  <Box h="100%" bg="blue.500" borderRadius="full" w={`${progressPercent}%`} transition="width 0.3s ease" />
                </Box>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme={isRemoving ? 'red' : 'blue'}
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText={`${isRemoving ? 'Removing' : 'Adding'}... ${progressPercent}%`}
            isDisabled={selectedTags.length === 0}
          >
            {isRemoving ? 'Remove from' : 'Add to'} {selectedDeals.length.toLocaleString()} Deals
          </Button>
        </ModalFooter>
      </ModalContent>

      {removalDialog}
    </Modal>
  );
}

export function BulkAssignModal({ isOpen, onClose, selectedDeals, selectedDealObjects, crmUsers, onSuccess }) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser } = useAuth();
  const toast = useToast();

  // Check if any selected deals are already assigned
  const hasAssignedDeals = selectedDealObjects?.some(deal => deal.ownerId && deal.ownerId !== '');
  const isReassignment = hasAssignedDeals;

  const handleUpdate = async () => {
    if (!selectedUserId) {
      toast({
        title: 'Select a user',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setIsUpdating(true);
    setProgress(0);
    
    try {
      const assignedUser = crmUsers.find(u => u.uid === selectedUserId);
      const userName = assignedUser?.name || 'user';

      if (isSupabaseConfigured()) {
        // Update deals in Supabase
        const { error: dealsError } = await supabase
          .from('crm_deals')
          .update({ owner_id: selectedUserId })
          .in('id', selectedDeals);

        if (dealsError) throw dealsError;

        // Create activity logs for each deal
        const activityInserts = selectedDeals.map(dealId => ({
          deal_id: dealId,
          author_id: currentUser.uid,
          type: 'transfer',
          content: `Deal transferred to ${userName} (Bulk Assignment)`,
          metadata: {
            authorName: currentUser.displayName || currentUser.email,
            previousOwnerId: 'bulk_reassignment', // We don't track prev owner per deal in bulk easily here without fetching
            newOwnerId: selectedUserId,
            isBulk: true
          },
          created_at: new Date().toISOString()
        }));

        const { error: activityError } = await supabase
          .from('crm_activities')
          .insert(activityInserts);

        if (activityError) throw activityError;

        // Update contacts for each deal
        const contactIds = selectedDealObjects
          ?.filter(d => d.contactId)
          .map(d => d.contactId);

        if (contactIds && contactIds.length > 0) {
          const { error: contactsError } = await supabase
            .from('crm_contacts')
            .update({ owner_id: selectedUserId })
            .in('id', contactIds);

          if (contactsError) throw contactsError;
        }

        // Add audit log
        await supabase.from('crm_audit_logs').insert({
          action: 'bulk_assignment',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'deal',
          details: {
            dealCount: selectedDeals.length,
            assignedTo: userName,
            assignedToId: selectedUserId,
            isReassignment: isReassignment,
            dealIds: selectedDeals,
          },
        });

        // Sync tag ownership so the new owner can filter by existing tags
        // This ensures users can filter by tags on their reassigned deals/contacts
        await syncTagOwnershipOnReassignment('deal', selectedDeals, selectedUserId);
        if (contactIds && contactIds.length > 0) {
          await syncTagOwnershipOnReassignment('contact', contactIds, selectedUserId);
        }
      }

      toast({
        title: isReassignment ? 'Deals reassigned' : 'Deals assigned',
        description: `${isReassignment ? 'Reassigned' : 'Assigned'} ${selectedDeals.length.toLocaleString()} deals to ${userName}`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      setSelectedUserId('');
      onClose();
    } catch (error) {
      console.error('Error assigning deals:', error);
      toast({
        title: 'Error assigning deals',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsUpdating(false);
      setProgress(0);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isUpdating ? undefined : onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{isReassignment ? 'Reassign' : 'Assign'} Deals (Bulk)</ModalHeader>
        {!isUpdating && <ModalCloseButton />}
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                This will {isReassignment ? 'reassign' : 'assign'} {selectedDeals.length.toLocaleString()} deals
              </Text>
            </Alert>

            <Select
              placeholder="Select loan officer"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              isDisabled={isUpdating}
            >
              {crmUsers.map((user) => (
                <option key={user.uid} value={user.uid}>
                  {user.name}
                </option>
              ))}
            </Select>
            
            {isUpdating && progress > 0 && (
              <VStack spacing={1} align="stretch">
                <Text fontSize="sm" color="gray.600">Assigning deals... {progress}%</Text>
                <Progress value={progress} size="sm" colorScheme="blue" borderRadius="md" />
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleUpdate}
            isLoading={isUpdating}
            loadingText={`Assigning... ${progress}%`}
          >
            {isReassignment ? 'Reassign' : 'Assign'} {selectedDeals.length.toLocaleString()} Deals
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Bulk Record Scope Modal (admin-only — gate the trigger with isCRMAdmin)
 * Marks selected deals as company records (visible to every CRM admin via the
 * visibility predicate) or converts them back to personal records. Writes with
 * _updated_by='import' so the audit trigger emits no deal.updated events —
 * scope flips must never fire automations.
 */
export function BulkDealRecordScopeModal({ isOpen, onClose, selectedDeals, onSuccess }) {
  const [scope, setScope] = useState('company');
  const [isUpdating, setIsUpdating] = useState(false);
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      if (isSupabaseConfigured()) {
        const CHUNK = 100;
        for (let i = 0; i < selectedDeals.length; i += CHUNK) {
          const chunk = selectedDeals.slice(i, i + CHUNK);
          const { error } = await supabase
            .from('crm_deals')
            .update({ record_scope: scope, _updated_by: 'import' })
            .in('id', chunk);
          if (error) throw error;
        }

        await supabase.from('crm_audit_logs').insert({
          action: scope === 'company' ? 'bulk_record_scope_company' : 'bulk_record_scope_personal',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'deal',
          details: {
            dealCount: selectedDeals.length,
            newScope: scope,
            dealIds: selectedDeals.slice(0, 200),
            truncated: selectedDeals.length > 200,
          },
        });
      }

      toast({
        title: scope === 'company' ? 'Marked as company records' : 'Converted to personal records',
        description: `${selectedDeals.length.toLocaleString()} deals updated`,
        status: 'success',
        duration: 4000,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating record scope:', error);
      toast({
        title: 'Error updating record scope',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={!isUpdating}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Set Record Scope (Bulk)</ModalHeader>
        <ModalCloseButton isDisabled={isUpdating} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" size="sm">
              <AlertIcon />
              <Text fontSize="sm">
                Company records stay on their owner's book but become visible to
                every CRM admin. This never enrolls anyone in campaigns.
              </Text>
            </Alert>
            <RadioGroup value={scope} onChange={setScope}>
              <VStack align="stretch" spacing={2}>
                <Radio value="company" isDisabled={isUpdating}>
                  <HStack spacing={2}>
                    <Text>Company record</Text>
                    <Badge colorScheme="purple">visible to all CRM admins</Badge>
                  </HStack>
                </Radio>
                <Radio value="user" isDisabled={isUpdating}>
                  <HStack spacing={2}>
                    <Text>Personal record</Text>
                    <Badge colorScheme="gray">owner + shares only</Badge>
                  </HStack>
                </Radio>
              </VStack>
            </RadioGroup>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isUpdating}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleUpdate} isLoading={isUpdating}>
            Update {selectedDeals.length.toLocaleString()} Deals
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function BulkDeleteModal({ isOpen, onClose, selectedDeals, onSuccess }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { currentUser } = useAuth();
  const toast = useToast();

  const handleDelete = async () => {
    setIsDeleting(true);
    setProgress(0);
    
    try {
      if (isSupabaseConfigured()) {
        // Delete deals in Supabase (activities will cascade delete)
        const { error } = await supabase
          .from('crm_deals')
          .delete()
          .in('id', selectedDeals);

        if (error) throw error;

        // Add audit log
        await supabase.from('crm_audit_logs').insert({
          action: 'bulk_delete',
          user_id: currentUser.uid,
          user_name: currentUser.displayName || currentUser.email,
          entity_type: 'deal',
          details: {
            dealCount: selectedDeals.length,
            dealIds: selectedDeals,
          },
        });
      }

      toast({
        title: 'Deals deleted',
        description: `Deleted ${selectedDeals.length.toLocaleString()} deals`,
        status: 'success',
        duration: 3000,
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error deleting deals:', error);
      toast({
        title: 'Error deleting deals',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
      setProgress(0);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={isDeleting ? undefined : onClose} size="md" isCentered closeOnOverlayClick={!isDeleting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Deals (Bulk)</ModalHeader>
        {!isDeleting && <ModalCloseButton />}
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="error">
              <AlertIcon />
              <Text fontSize="sm">
                This will permanently delete {selectedDeals.length.toLocaleString()} deals. This action cannot be undone.
              </Text>
            </Alert>
            
            {isDeleting && progress > 0 && (
              <VStack spacing={1} align="stretch">
                <Text fontSize="sm" color="gray.600">Deleting deals... {progress}%</Text>
                <Progress value={progress} size="sm" colorScheme="red" borderRadius="md" />
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isDeleting}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isLoading={isDeleting}
            loadingText={`Deleting... ${progress}%`}
          >
            Delete {selectedDeals.length.toLocaleString()} Deals
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

